import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

// ---------------------------------------------------------------------------
// Excel serial date helpers
// ---------------------------------------------------------------------------

// Excel epoch: serial 1 = January 1, 1900.
// Excel incorrectly treats 1900 as a leap year (serial 60 = Feb 29, 1900).
// We maintain this bug: serials >= 61 are offset by 1 relative to the real
// proleptic Gregorian calendar.

/**
 * Convert a calendar date to an Excel serial number.
 * Matches Excel's behaviour including the 1900 leap-year bug.
 */
function dateToSerial(year: number, month: number, day: number): number {
    // Normalise month overflow/underflow
    month = Math.floor(month);
    day = Math.floor(day);
    year = Math.floor(year);

    // Let JavaScript normalise the date (handles month/day overflow)
    const d = new Date(year, month - 1, day);
    // Use UTC to avoid DST issues when computing day offsets
    const utc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    // Excel serial for 1900-01-01 is 1
    // JS: Date.UTC(1899,11,31) is our "day 0" baseline
    const baseline = Date.UTC(1899, 11, 31); // Dec 31, 1899
    const serial = Math.round((utc - baseline) / 86400000);

    // Excel inserts a phantom Feb 29 1900 at serial 60
    if (serial >= 60) return serial + 1;
    return serial;
}

interface SerialDate {
    year: number;
    month: number;
    day: number;
}

/**
 * Convert an Excel serial number to a calendar date.
 * Handles the 1900 leap-year bug.
 */
function serialToDate(serial: number): SerialDate {
    const s = Math.floor(serial);

    // Serial 60 is Excel's phantom Feb 29, 1900
    if (s === 60) return { year: 1900, month: 2, day: 29 };

    // Adjust for the phantom day
    const adjusted = s >= 61 ? s - 1 : s;

    const baseline = Date.UTC(1899, 11, 31); // Dec 31 1899 = day 0
    const ms = baseline + adjusted * 86400000;
    const d = new Date(ms);

    return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate()
    };
}

/**
 * Convert hours/minutes/seconds to a fractional day (time serial).
 */
function timeToSerial(hours: number, minutes: number, seconds: number): number {
    return (hours * 3600 + minutes * 60 + seconds) / 86400;
}

/** Extract the integer (date) part of a serial number. */
function serialDatePart(serial: number): number {
    return Math.floor(serial);
}

/** Extract the fractional (time) part of a serial number. */
function serialTimePart(serial: number): number {
    return serial - Math.floor(serial);
}

/** Build a JS Date (UTC) from an Excel serial's date part. */
function serialToUTCDate(serial: number): Date {
    const { year, month, day } = serialToDate(serialDatePart(serial));
    return new Date(Date.UTC(year, month - 1, day));
}

/** True if the given year is a leap year (proleptic Gregorian). */
function isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Number of days in a month. */
function daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

/** Parse a weekend mask string (e.g. "0000011") or return a number mode. */
function parseWeekend(weekend: FormulaValue): Set<number> {
    // weekend days: 0=Sun,1=Mon,...,6=Sat  (JS getUTCDay convention)
    const weekendModes: Record<number, number[]> = {
        1: [0, 6], // Sat, Sun (default)
        2: [0, 1], // Sun, Mon
        3: [1, 2], // Mon, Tue
        4: [2, 3], // Tue, Wed
        5: [3, 4], // Wed, Thu
        6: [4, 5], // Thu, Fri
        7: [5, 6], // Fri, Sat
        11: [0], // Sun only
        12: [1], // Mon only
        13: [2], // Tue only
        14: [3], // Wed only
        15: [4], // Thu only
        16: [5], // Fri only
        17: [6], // Sat only
    };

    if (typeof weekend === 'string' && weekend.length === 7) {
        // "1" means weekend day, position 0=Mon,...,6=Sun
        const days: number[] = [];
        for (let i = 0; i < 7; i++) {
            if (weekend[i] === '1') {
                // Convert Monday-first index to JS Sun=0 convention
                days.push((i + 1) % 7);
            }
        }
        return new Set(days);
    }

    const mode = toNum(weekend);
    const days = weekendModes[mode] ?? weekendModes[1];
    return new Set(days);
}

/** Build a Set of holiday serial integers. */
function buildHolidaySet(holidays: FormulaValue): Set<number> {
    const set = new Set<number>();
    if (holidays === null || holidays === undefined) return set;
    const flat = flattenArgs([holidays]);
    for (const h of flat) {
        set.add(Math.floor(h));
    }
    return set;
}

// ---------------------------------------------------------------------------

export function registerDateFunctions(registry: FunctionRegistry): void {
    // DATE(year, month, day): returns an Excel serial number
    registry.register('DATE', (...args: FormulaValue[]): FormulaValue => {
        const year = toNum(args[0]);
        const month = toNum(args[1]);
        const day = toNum(args[2]);
        return dateToSerial(Math.floor(year), Math.floor(month), Math.floor(day));
    });

    // DATEDIF(startDate, endDate, unit)
    registry.register('DATEDIF', (...args: FormulaValue[]): FormulaValue => {
        const start = serialToDate(Math.floor(toNum(args[0])));
        const end = serialToDate(Math.floor(toNum(args[1])));
        const unit = toStr(args[2]).toUpperCase();

        const sy = start.year; const sm = start.month; const sd = start.day;
        const ey = end.year; const em = end.month; const ed = end.day;

        switch (unit) {
            case 'Y':
                return ey - sy - (em < sm || (em === sm && ed < sd) ? 1 : 0);
            case 'M': {
                let months = (ey - sy) * 12 + (em - sm);
                if (ed < sd) months--;
                return months;
            }
            case 'D':
                return Math.floor(toNum(args[1])) - Math.floor(toNum(args[0]));
            case 'YM': {
                let months = em - sm;
                if (ed < sd) months--;
                if (months < 0) months += 12;
                return months;
            }
            case 'YD': {
                // Days between dates as if same year
                let startSerial = dateToSerial(ey, sm, sd);
                if (startSerial > Math.floor(toNum(args[1]))) {
                    startSerial = dateToSerial(ey - 1, sm, sd);
                }
                return Math.floor(toNum(args[1])) - startSerial;
            }
            case 'MD': {
                // Days ignoring months and years
                let days = ed - sd;
                if (days < 0) {
                    const prevMonth = em === 1 ? 12 : em - 1;
                    const prevYear = em === 1 ? ey - 1 : ey;
                    days += daysInMonth(prevYear, prevMonth);
                }
                return days;
            }
            default:
                throw new FormulaError(FormulaErrorType.VALUE, `DATEDIF: unknown unit "${unit}"`);
        }
    });

    // DATEVALUE(dateText): parse "DD/MM/YYYY" to serial
    registry.register('DATEVALUE', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]).trim();
        // Try DD/MM/YYYY
        const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
        if (!match) {
            throw new FormulaError(FormulaErrorType.VALUE, `DATEVALUE: cannot parse "${text}"`);
        }
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        let year = parseInt(match[3], 10);
        if (year < 100) year += year < 30 ? 2000 : 1900;
        return dateToSerial(year, month, day);
    });

    // DAY(serialNumber): extract day component
    registry.register('DAY', (...args: FormulaValue[]): FormulaValue => {
        return serialToDate(serialDatePart(toNum(args[0]))).day;
    });

    // DAYS(endDate, startDate): difference in days
    registry.register('DAYS', (...args: FormulaValue[]): FormulaValue => {
        const end = Math.floor(toNum(args[0]));
        const start = Math.floor(toNum(args[1]));
        return end - start;
    });

    // DAYS360(startDate, endDate, method?): 30/360 day count
    registry.register('DAYS360', (...args: FormulaValue[]): FormulaValue => {
        const s = serialToDate(serialDatePart(toNum(args[0])));
        const e = serialToDate(serialDatePart(toNum(args[1])));
        const method = args[2] !== undefined && args[2] !== null ? Boolean(toNum(args[2])) : false;

        let sd = s.day; const sm = s.month; const sy = s.year;
        let ed = e.day; const em = e.month; const ey = e.year;

        if (method) {
            // European method: clamp both to 30
            if (sd === 31) sd = 30;
            if (ed === 31) ed = 30;
        } else {
            // US method
            if (sd === 31) sd = 30;
            if (ed === 31 && sd === 30) ed = 30;
            // End of February
            if (sm === 2 && sd === daysInMonth(sy, 2)) sd = 30;
            if (em === 2 && ed === daysInMonth(ey, 2)) ed = 30;
        }

        return (ey - sy) * 360 + (em - sm) * 30 + (ed - sd);
    });

    // EDATE(startDate, months): add months to a date
    registry.register('EDATE', (...args: FormulaValue[]): FormulaValue => {
        const { year, month, day } = serialToDate(serialDatePart(toNum(args[0])));
        const months = Math.floor(toNum(args[1]));
        const newDate = new Date(Date.UTC(year, month - 1 + months, day));
        return dateToSerial(newDate.getUTCFullYear(), newDate.getUTCMonth() + 1, newDate.getUTCDate());
    });

    // EOMONTH(startDate, months): last day of month after adding months
    registry.register('EOMONTH', (...args: FormulaValue[]): FormulaValue => {
        const { year, month } = serialToDate(serialDatePart(toNum(args[0])));
        const months = Math.floor(toNum(args[1]));
        const targetMonth = month + months;
        // Day 0 of (targetMonth+1) = last day of targetMonth
        const d = new Date(Date.UTC(year, targetMonth, 0));
        return dateToSerial(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
    });

    // HOUR(serialNumber): extract hour from fractional time part
    registry.register('HOUR', (...args: FormulaValue[]): FormulaValue => {
        const frac = serialTimePart(toNum(args[0]));
        return Math.floor(frac * 24) % 24;
    });

    // ISOWEEKNUM(date): ISO 8601 week number
    registry.register('ISOWEEKNUM', (...args: FormulaValue[]): FormulaValue => {
        const d = serialToUTCDate(toNum(args[0]));
        // ISO week: week containing the first Thursday of the year
        const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
        const startOfWeek1 = jan4.getTime() - ((jan4.getUTCDay() || 7) - 1) * 86400000;
        const weekNum = Math.floor((d.getTime() - startOfWeek1) / (7 * 86400000)) + 1;
        if (weekNum < 1) {
            // Belongs to last week of previous year
            const dec28 = new Date(Date.UTC(d.getUTCFullYear() - 1, 11, 28));
            const jan4Prev = new Date(Date.UTC(d.getUTCFullYear() - 1, 0, 4));
            const startPrev = jan4Prev.getTime() - ((jan4Prev.getUTCDay() || 7) - 1) * 86400000;
            return Math.floor((dec28.getTime() - startPrev) / (7 * 86400000)) + 1;
        }
        // Check if it belongs to week 1 of next year
        const dec28 = new Date(Date.UTC(d.getUTCFullYear(), 11, 28));
        const jan4Next = new Date(Date.UTC(d.getUTCFullYear() + 1, 0, 4));
        const startNext = jan4Next.getTime() - ((jan4Next.getUTCDay() || 7) - 1) * 86400000;
        if (d.getTime() >= startNext) return 1;
        return weekNum;
    });

    // MINUTE(serialNumber): extract minute from fractional time part
    registry.register('MINUTE', (...args: FormulaValue[]): FormulaValue => {
        const frac = serialTimePart(toNum(args[0]));
        const totalSeconds = Math.round(frac * 86400);
        return Math.floor(totalSeconds / 60) % 60;
    });

    // MONTH(serialNumber): extract month component
    registry.register('MONTH', (...args: FormulaValue[]): FormulaValue => {
        return serialToDate(serialDatePart(toNum(args[0]))).month;
    });

    // NETWORKDAYS(startDate, endDate, holidays?): count business days (Mon-Fri)
    registry.register('NETWORKDAYS', (...args: FormulaValue[]): FormulaValue => {
        const start = Math.floor(toNum(args[0]));
        const end = Math.floor(toNum(args[1]));
        const holidays = buildHolidaySet(args[2] ?? null);

        const weekendDays = new Set([0, 6]); // Sun, Sat
        let count = 0;
        const step = start <= end ? 1 : -1;

        for (let s = start; step > 0 ? s <= end : s >= end; s += step) {
            const d = serialToUTCDate(s);
            const dow = d.getUTCDay();
            if (!weekendDays.has(dow) && !holidays.has(s)) count++;
        }

        return step > 0 ? count : -count;
    });

    // NETWORKDAYS.INTL(startDate, endDate, weekend?, holidays?)
    registry.register('NETWORKDAYS.INTL', (...args: FormulaValue[]): FormulaValue => {
        const start = Math.floor(toNum(args[0]));
        const end = Math.floor(toNum(args[1]));
        const weekend = args[2] !== undefined && args[2] !== null ? args[2] : 1;
        const holidays = buildHolidaySet(args[3] ?? null);

        const weekendDays = parseWeekend(weekend);
        let count = 0;
        const step = start <= end ? 1 : -1;

        for (let s = start; step > 0 ? s <= end : s >= end; s += step) {
            const d = serialToUTCDate(s);
            const dow = d.getUTCDay();
            if (!weekendDays.has(dow) && !holidays.has(s)) count++;
        }

        return step > 0 ? count : -count;
    });

    // NOW(): current date+time serial
    registry.register('NOW', (..._args: FormulaValue[]): FormulaValue => {
        const now = new Date();
        const datePart = dateToSerial(now.getFullYear(), now.getMonth() + 1, now.getDate());
        const timePart = timeToSerial(now.getHours(), now.getMinutes(), now.getSeconds());
        return smartRound(datePart + timePart);
    });

    // SECOND(serialNumber): extract second from fractional time part
    registry.register('SECOND', (...args: FormulaValue[]): FormulaValue => {
        const frac = serialTimePart(toNum(args[0]));
        const totalSeconds = Math.round(frac * 86400);
        return totalSeconds % 60;
    });

    // TIME(hour, minute, second): fractional day serial
    registry.register('TIME', (...args: FormulaValue[]): FormulaValue => {
        const h = toNum(args[0]);
        const m = toNum(args[1]);
        const s = toNum(args[2]);
        const frac = timeToSerial(h, m, s);
        // Normalise to [0, 1)
        return smartRound(((frac % 1) + 1) % 1);
    });

    // TIMEVALUE(timeText): parse a time string to a fractional day
    registry.register('TIMEVALUE', (...args: FormulaValue[]): FormulaValue => {
        const text = toStr(args[0]).trim();
        // Supports HH:MM:SS and HH:MM
        const match = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i);
        if (!match) {
            throw new FormulaError(FormulaErrorType.VALUE, `TIMEVALUE: cannot parse "${text}"`);
        }

        let hours = parseInt(match[1], 10);
        const mins = parseInt(match[2], 10);
        const secs = match[3] !== undefined ? parseInt(match[3], 10) : 0;
        const ampm = match[4]?.toUpperCase();

        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;

        return smartRound(timeToSerial(hours, mins, secs));
    });

    // TODAY(): current date serial (no time fraction)
    registry.register('TODAY', (..._args: FormulaValue[]): FormulaValue => {
        const now = new Date();
        return dateToSerial(now.getFullYear(), now.getMonth() + 1, now.getDate());
    });

    // WEEKDAY(serialNumber, returnType?): day of week
    registry.register('WEEKDAY', (...args: FormulaValue[]): FormulaValue => {
        const d = serialToUTCDate(toNum(args[0]));
        const returnType = args[1] !== undefined && args[1] !== null ? toNum(args[1]) : 1;
        const dow = d.getUTCDay(); // 0=Sun ... 6=Sat

        switch (Math.floor(returnType)) {
            case 1: return dow + 1; // 1=Sun ... 7=Sat
            case 2: return ((dow + 6) % 7) + 1; // 1=Mon ... 7=Sun
            case 3: return (dow + 6) % 7; // 0=Mon ... 6=Sun
            case 11: return ((dow + 6) % 7) + 1; // same as 2
            case 12: return ((dow + 5) % 7) + 1; // 1=Tue
            case 13: return ((dow + 4) % 7) + 1; // 1=Wed
            case 14: return ((dow + 3) % 7) + 1; // 1=Thu
            case 15: return ((dow + 2) % 7) + 1; // 1=Fri
            case 16: return ((dow + 1) % 7) + 1; // 1=Sat
            case 17: return dow + 1; // 1=Sun (same as 1)
            default:
                throw new FormulaError(FormulaErrorType.VALUE, 'WEEKDAY: invalid returnType');
        }
    });

    // WEEKNUM(serialNumber, returnType?): week number
    registry.register('WEEKNUM', (...args: FormulaValue[]): FormulaValue => {
        const d = serialToUTCDate(toNum(args[0]));
        const returnType = args[1] !== undefined && args[1] !== null ? Math.floor(toNum(args[1])) : 1;

        if (returnType === 21) {
            // ISO week number — delegate
            const jan4 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
            const startOfWeek1 = jan4.getTime() - ((jan4.getUTCDay() || 7) - 1) * 86400000;
            return Math.floor((d.getTime() - startOfWeek1) / (7 * 86400000)) + 1;
        }

        // Week starts on Sunday (returnType=1) or Monday (returnType=2)
        const firstDay = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        const firstDow = firstDay.getUTCDay(); // 0=Sun

        let startOffset: number;
        if (returnType === 2 || (returnType >= 11 && returnType <= 17)) {
            // Week starts Monday
            startOffset = (firstDow + 6) % 7; // days from Monday to Jan 1
        } else {
            // Week starts Sunday
            startOffset = firstDow;
        }

        const dayOfYear = Math.floor((d.getTime() - firstDay.getTime()) / 86400000);
        return Math.floor((dayOfYear + startOffset) / 7) + 1;
    });

    // WORKDAY(startDate, days, holidays?): add business days
    registry.register('WORKDAY', (...args: FormulaValue[]): FormulaValue => {
        let current = Math.floor(toNum(args[0]));
        let remaining = Math.floor(toNum(args[1]));
        const holidays = buildHolidaySet(args[2] ?? null);
        const weekendDays = new Set([0, 6]);
        const step = remaining >= 0 ? 1 : -1;

        while (remaining !== 0) {
            current += step;
            const dow = serialToUTCDate(current).getUTCDay();
            if (!weekendDays.has(dow) && !holidays.has(current)) {
                remaining -= step;
            }
        }
        return current;
    });

    // WORKDAY.INTL(startDate, days, weekend?, holidays?)
    registry.register('WORKDAY.INTL', (...args: FormulaValue[]): FormulaValue => {
        let current = Math.floor(toNum(args[0]));
        let remaining = Math.floor(toNum(args[1]));
        const weekend = args[2] !== undefined && args[2] !== null ? args[2] : 1;
        const holidays = buildHolidaySet(args[3] ?? null);
        const weekendDays = parseWeekend(weekend);
        const step = remaining >= 0 ? 1 : -1;

        while (remaining !== 0) {
            current += step;
            const dow = serialToUTCDate(current).getUTCDay();
            if (!weekendDays.has(dow) && !holidays.has(current)) {
                remaining -= step;
            }
        }
        return current;
    });

    // YEAR(serialNumber): extract year component
    registry.register('YEAR', (...args: FormulaValue[]): FormulaValue => {
        return serialToDate(serialDatePart(toNum(args[0]))).year;
    });

    // YEARFRAC(startDate, endDate, basis?): fraction of year between two dates
    registry.register('YEARFRAC', (...args: FormulaValue[]): FormulaValue => {
        const start = Math.floor(toNum(args[0]));
        const end = Math.floor(toNum(args[1]));
        const basis = args[2] !== undefined && args[2] !== null ? Math.floor(toNum(args[2])) : 0;

        const s = serialToDate(start);
        const e = serialToDate(end);

        switch (basis) {
            case 0: {
                // US (NASD) 30/360
                let sd = s.day; const sm = s.month; const sy = s.year;
                let ed = e.day; const em = e.month; const ey = e.year;
                if (sd === 31) sd = 30;
                if (ed === 31 && sd === 30) ed = 30;
                const days360 = (ey - sy) * 360 + (em - sm) * 30 + (ed - sd);
                return smartRound(days360 / 360);
            }
            case 1: {
                // Actual/actual
                const days = end - start;
                const avgYear = ((isLeapYear(s.year) ? 366 : 365) + (isLeapYear(e.year) ? 366 : 365)) / 2;
                return smartRound(days / avgYear);
            }
            case 2:
                // Actual/360
                return smartRound((end - start) / 360);
            case 3:
                // Actual/365
                return smartRound((end - start) / 365);
            case 4: {
                // European 30/360
                let sd4 = s.day; const sm4 = s.month; const sy4 = s.year;
                let ed4 = e.day; const em4 = e.month; const ey4 = e.year;
                if (sd4 === 31) sd4 = 30;
                if (ed4 === 31) ed4 = 30;
                const days4 = (ey4 - sy4) * 360 + (em4 - sm4) * 30 + (ed4 - sd4);
                return smartRound(days4 / 360);
            }
            default:
                throw new FormulaError(FormulaErrorType.VALUE, 'YEARFRAC: invalid basis');
        }
    });

    // Keep the unused-import linter satisfied
    void toStr;
}
